// src/context/ClientsProvider.js
import React, { useState, useEffect, useContext, useRef } from "react";
import api from "../../api/axios";
import { clearClientTokenForCase } from "../../api/axios";
import { ClientContext } from "./Context";
import { Outlet } from "react-router-dom";
import Swal from "sweetalert2";
import LoadingMainPage from "../Common/Loading/LoadingMainPage";
export const ClientsProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [clientIsLoggedIn, setClientIsLoggedIn] = useState(false);
  const [clientIsLoading, setClientIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isLoggedInRef = useRef(clientIsLoggedIn);

  const getCurrentCaseId = () => {
    try {
      const pathname = window.location.pathname || "";
      if (pathname.startsWith("/client-sign")) {
        const params = new URLSearchParams(window.location.search || "");
        return params.get("id");
      }

      const hash = window.location.hash || "";
      if (
        hash.startsWith("#/client-sign") ||
        hash.startsWith("#//client-sign")
      ) {
        const hashQuery = hash.includes("?") ? hash.split("?")[1] : "";
        if (!hashQuery) return null;
        const hashParams = new URLSearchParams(hashQuery);
        return hashParams.get("id");
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    isLoggedInRef.current = clientIsLoggedIn;
  }, [clientIsLoggedIn]);

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Obtener usuario desde backend
  const fetchUserData = async () => {
    const url = `${process.env.REACT_APP_API_URL}`;
    try {
      const response = await api.get(url + "/getclient");
      const { user, userCompany } = response.data || {};

      if (user) {
        setUserData({ user, userCompany });
        setClientIsLoggedIn(true);
      } else {
        console.log("[ClientsProvider] /getclient sin usuario");
        setUserData(null);
        setClientIsLoggedIn(false);
      }
    } catch (err) {
      if (err?.response?.data?.code === "SESSION_CASE_MISMATCH") {
        console.log(
          "[ClientsProvider] Sesión no válida para este expediente. Se requiere nuevo OTP.",
        );
      }
      console.warn("[ClientsProvider] Error al hidratar sesión cliente", {
        status: err?.response?.status,
        data: err?.response?.data,
      });
      setUserData(null);
      setClientIsLoggedIn(false);
    } finally {
      setClientIsLoading(false);
    }
  };

  // const logout = async (showConfirm = false, auto = false) => {
  //   const doLogout = async () => {
  //     try {
  //       // Solo llamar al backend si estamos realmente logueados (evita 500 si no hay cookie)
  //       if (clientIsLoggedIn.current) {
  //         await api.post("/logout");
  //       }
  //     } catch (err) {
  //       console.warn("[UserProvider] Logout backend falló", err);
  //     }

  //     // Limpiar estado React SIEMPRE
  //     setUserData({ user: null, userCompany: null });
  //     setClientIsLoggedIn(false);

  //     if (auto) {
  //       // Mostrar aviso antes de redirigir (automático)
  //       Swal.fire({
  //         title: "Sesión expirada",
  //         text: "Tu sesión ha caducado, por favor vuelve a iniciar sesión.",
  //         icon: "warning",
  //         timer: 2000,
  //         showConfirmButton: false,
  //         allowOutsideClick: false,
  //       }).then(() => {
  //         sessionStorage.clear();
  //         localStorage.clear();
  //         window.location.reload();
  //       });
  //     }
  //   };

  //   if (showConfirm) {
  //     const result = await Swal.fire({
  //       title: "Cerrar sesión",
  //       icon: "warning",
  //       html: `<p>¡Estás a punto de salir!</p><b>¿Deseas continuar?</b>`,
  //       showCancelButton: true,
  //       confirmButtonText: "Sí",
  //       confirmButtonColor: "#1ab394",
  //       cancelButtonText: "No, volver",
  //       cancelButtonColor: "#d33",
  //       reverseButtons: true,
  //     });

  //     if (result.isConfirmed) {
  //       doLogout();
  //     }
  //   } else {
  //     doLogout();
  //   }
  // };

  // Logout unificado (manual o automático). Flag 'auto' opcional.
  const logout = async (showConfirm = false, auto = false) => {
    const doLogout = async () => {
      setIsLoggingOut(true);

      try {
        await Promise.allSettled([
          isLoggedInRef.current
            ? api.post("/logout", { scope: "client" })
            : Promise.resolve(),
          wait(2000),
        ]);
      } catch (err) {
        console.warn("[UserProvider] Logout backend falló", err);
      }

      if (auto) {
        await Swal.fire({
          title: "Sesión expirada",
          text: "Tu sesión ha caducado, por favor vuelve a iniciar sesión.",
          icon: "warning",
          timer: 2000,
          showConfirmButton: false,
          allowOutsideClick: false,
        });
      }

      // 🧹 Limpieza total
      sessionStorage.clear();
      localStorage.clear();

      //  Limpiar estado React SIEMPRE
      setUserData({ user: null, userCompany: null });
      setClientIsLoggedIn(false);

      const currentCaseId = getCurrentCaseId();
      if (currentCaseId) {
        clearClientTokenForCase(currentCaseId);
        console.log(
          "[ClientsProvider] Token de cliente eliminado para expediente",
          {
            case_id: currentCaseId,
          },
        );
      }
      setIsLoggingOut(false);

      // window.location.replace("/");
    };

    if (showConfirm) {
      const result = await Swal.fire({
        title: "Cerrar sesión",
        icon: "warning",
        html: `<b>¿Deseas continuar?</b>`,
        showCancelButton: true,
        confirmButtonText: "Sí",
        confirmButtonColor: "#1ab394",
        cancelButtonText: "No, volver",
        cancelButtonColor: "#d33",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        doLogout();
      }
    } else {
      doLogout();
    }
  };

  useEffect(() => {
    // No limpiar cookie cliente al montar para no romper el flujo OTP -> firma -> FI.
    fetchUserData();
  }, []);

  return (
    <ClientContext.Provider
      value={{
        userData,
        clientIsLoggedIn,
        clientIsLoading,
        fetchUserData,
        logout,
      }}
    >
      {/* {children} */}
      {children ? children : <Outlet />}
      <LoadingMainPage
        isLoading={isLoggingOut}
        logoVariant="letters"
        minVisibleMs={4000}
        statusText="Cerrando sesión"
      />
    </ClientContext.Provider>
  );
};

export const useUser = () => useContext(ClientContext);
